import { createApi, fetchBaseQuery, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { getApiConfig } from '@/config/api';
import { getRequestLanguage } from '@/utils/requestLanguage';
import type { ApiResponse } from '@/types';
import { subscriptionApi } from './subscriptionApi';

// Outlet Types
export interface Outlet {
  id: string;
  storeId: string;
  name: string;
  code?: string | null;
  address?: string | null;
  phoneNumber?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  store?: {
    id: string;
    name: string;
    ownerId: string;
  };
  _count?: {
    transactions: number;
    itemStocks: number;
    expenses: number;
    cashDrawers: number;
  };
}

export interface CreateOutletDto {
  name: string;
  code?: string;
  address?: string;
  phoneNumber?: string;
  isActive?: boolean;
}

export interface UpdateOutletDto {
  name?: string;
  code?: string;
  address?: string;
  phoneNumber?: string;
  isActive?: boolean;
}

export interface OutletsResponse {
  outlets: Outlet[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// Create a dynamic base query for outlet API
const dynamicOutletBaseQuery: BaseQueryFn = async (args, api, extraOptions) => {
  const config = getApiConfig();

  const baseQuery = fetchBaseQuery({
    baseUrl: config.API_URL,
    prepareHeaders: (headers, { getState }) => {
      let token = (getState() as any).auth.token;
      if (!token) {
        token = localStorage.getItem('token');
      }
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      headers.set('ngrok-skip-browser-warning', 'true');
      headers.set('accept-language', getRequestLanguage());
      return headers;
    },
  });

  return baseQuery(args, api, extraOptions);
};

export const outletApi = createApi({
  reducerPath: 'outletApi',
  baseQuery: dynamicOutletBaseQuery,
  tagTypes: ['Outlet'],
  endpoints: (builder) => ({
    // Get all outlets for a store
    getOutlets: builder.query<ApiResponse<Outlet[]>, {
      storeId: string;
      page?: number;
      limit?: number;
      search?: string;
      isActive?: boolean;
    }>({
      query: (params) => ({
        url: '/outlets',
        params,
      }),
      providesTags: ['Outlet'],
    }),

    // Get single outlet
    getOutlet: builder.query<ApiResponse<Outlet>, string>({
      query: (id) => `/outlets/${id}`,
      providesTags: (_, __, id) => [{ type: 'Outlet', id }],
    }),

    // Create outlet
    createOutlet: builder.mutation<
      ApiResponse<Outlet>,
      { storeId: string; data: CreateOutletDto }
    >({
      query: ({ storeId, data }) => ({
        url: '/outlets',
        method: 'POST',
        body: { ...data, storeId },
      }),
      invalidatesTags: ['Outlet'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(subscriptionApi.util.invalidateTags(["Subscription"]));
        } catch {
          // Mutation errors are handled by the caller
        }
      },
    }),

    // Update outlet
    updateOutlet: builder.mutation<
      ApiResponse<Outlet>,
      { id: string; data: UpdateOutletDto }
    >({
      query: ({ id, data }) => ({
        url: `/outlets/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'Outlet', id }, 'Outlet'],
    }),

    // Delete outlet
    deleteOutlet: builder.mutation<ApiResponse<{ message: string }>, { id: string; force?: boolean }>({
      query: ({ id, force }) => ({
        url: `/outlets/${id}`,
        method: 'DELETE',
        params: force ? { force: 'true' } : undefined,
      }),
      invalidatesTags: ['Outlet'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(subscriptionApi.util.invalidateTags(["Subscription"]));
        } catch {
          // Mutation errors are handled by the caller
        }
      },
    }),

    // Activate outlet
    activateOutlet: builder.mutation<ApiResponse<Outlet>, string>({
      query: (id) => ({
        url: `/outlets/${id}/activate`,
        method: 'PATCH',
      }),
      invalidatesTags: (_, __, id) => [{ type: 'Outlet', id }, 'Outlet'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(subscriptionApi.util.invalidateTags(["Subscription"]));
        } catch {
          // Mutation errors are handled by the caller
        }
      },
    }),

    // Deactivate outlet
    deactivateOutlet: builder.mutation<ApiResponse<Outlet>, string>({
      query: (id) => ({
        url: `/outlets/${id}/deactivate`,
        method: 'PATCH',
      }),
      invalidatesTags: (_, __, id) => [{ type: 'Outlet', id }, 'Outlet'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(subscriptionApi.util.invalidateTags(["Subscription"]));
        } catch {
          // Mutation errors are handled by the caller
        }
      },
    }),
  }),
});

export const {
  useGetOutletsQuery,
  useGetOutletQuery,
  useCreateOutletMutation,
  useUpdateOutletMutation,
  useDeleteOutletMutation,
  useActivateOutletMutation,
  useDeactivateOutletMutation,
} = outletApi;
